import { useEffect, useMemo, useState } from "react";
import { Maximize2, Minimize2, Pencil, Save, X } from "lucide-react";
import {
  type Annotation,
  addAnnotation,
  deleteAnnotation,
  getDocument,
  mergeDocumentSections,
  saveDocumentContent,
  setAnnotationResolved,
  splitDocumentSections,
} from "../docClient";
import { AnnotatedMarkdown, AnnotationSidebar } from "./AnnotatedMarkdown";
import PromptPane from "./PromptPane";
import type { AuthUser } from "../authClient";
import type { Chapter } from "../data";
import { chapterHeading, documentOrder } from "../chapterLayout";

/** 找出 `quote` 在 `text` 中所有（不重叠的）出现位置，按出现顺序排列。 */
function findOccurrences(text: string, quote: string): number[] {
  const hits: number[] = [];
  let from = 0;
  while (true) {
    const idx = text.indexOf(quote, from);
    if (idx === -1) break;
    hits.push(idx);
    from = idx + quote.length;
  }
  return hits;
}

/** 把一段 marked 渲染出的 HTML 还原成纯文本，用于按纯文本内容判断批注引用是否还
 *  存在于文档里（而不是被 `**`、`<br>` 这类标记语法隔断导致误判"不存在"）。 */
function toPlainText(html: string): string {
  const container = document.createElement("div");
  container.innerHTML = html;
  let text = "";
  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.nodeValue ?? "";
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      if ((node as Element).tagName === "BR") {
        text += "\n";
        return;
      }
      node.childNodes.forEach(walk);
    }
  }
  walk(container);
  return text;
}

/** 在"完整文档"视图下创建的批注（以及加 `chapter_num` 字段之前的历史数据）没有
 *  记录所属章节，这里通过引用文本在全文纯文本中第 `quote_index` 次出现的位置，
 *  定位它落在哪个 `## 编号 标题` 章节的正文范围内；同时换算出这次出现是"本章内的
 *  第几次出现"（`localIndex`）——因为 `quote_index` 记录的是在全文里的第几次，
 *  单章预览时渲染的只是这一章自己的内容，两者的"第几次出现"坐标系不一样：如果这句
 *  话在别的章节也出现过、且在本章内又不止出现一次，直接把全文的序号交给本章去查，
 *  可能会错位标到本章里的另一次出现上。
 *
 *  定位所属章节这一步本身也是必要的：同一段文字完全可能在不同章节里重复出现（比如
 *  药物名称、常见表述），单靠"这段文字是否出现在本章内容里"判断不了它到底是哪一章
 *  的批注 —— 那样会导致第一章加的批注，只要第二章里也出现了同样的文字，就会被误认
 *  为也属于第二章。 */
function locateChapterForAnnotation(
  a: Annotation,
  fullPlainText: string,
  chapters: Chapter[]
): { chapterNum: string; localIndex: number } | null {
  const marks: { num: string; headingStart: number; contentStart: number }[] = [];
  let searchFrom = 0;
  for (const c of documentOrder(chapters)) {
    const heading = chapterHeading(c);
    const idx = fullPlainText.indexOf(heading, searchFrom);
    if (idx === -1) continue;
    marks.push({ num: c.num, headingStart: idx, contentStart: idx + heading.length });
    searchFrom = idx + heading.length;
  }
  const bounds = marks.map((m, i) => ({
    num: m.num,
    start: m.contentStart,
    end: i + 1 < marks.length ? marks[i + 1]!.headingStart : fullPlainText.length,
  }));

  const quote = a.quote.trim();
  const hits = findOccurrences(fullPlainText, quote);
  if (!hits.length) return null;
  const globalIndex = Math.min(Math.max(a.quote_index ?? 0, 0), hits.length - 1);
  const pos = hits[globalIndex]!;
  const bound = bounds.find((b) => pos >= b.start && pos < b.end);
  if (!bound) return null;
  const hitsInChapter = hits.filter((h) => h >= bound.start && h < bound.end);
  return { chapterNum: bound.num, localIndex: hitsInChapter.indexOf(pos) };
}

export default function DocumentEditor({
  conversationId,
  chapters,
  chapter,
  user,
  onPromptSaved,
  onClose,
}: {
  conversationId: string;
  /** 全部章节列表，用于按 `## 编号 标题` 拆分/拼装整份文档。 */
  chapters: Chapter[];
  /** 指定则只预览/编辑这一章；不传则是整份文档。 */
  chapter?: Chapter | null;
  user: AuthUser;
  /** 管理员在「提示词」页改了提示词后，把新内容回传给上层刷新 chapters。 */
  onPromptSaved: (chapterNum: string, prompt: string) => void;
  onClose: () => void;
}) {
  const [fullContent, setFullContent] = useState("");
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeAnnoId, setActiveAnnoId] = useState<number | null>(null);
  const [scrollToId, setScrollToId] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // 只有单章视图才有「提示词」页：整份文档对应的是全部章节，没有唯一的提示词。
  const [tab, setTab] = useState<"doc" | "prompt">("doc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDocument(conversationId)
      .then(({ document, annotations }) => {
        if (cancelled) return;
        setFullContent(document.content);
        setAnnotations(annotations);
        if (chapter) {
          const section = splitDocumentSections(document.content, chapters).find((s) => s.num === chapter.num);
          setContent(section?.body ?? "");
          setSavedContent(section?.body ?? "");
          // 本章还没有正文时直接落在提示词页，省得用户点进来先看到一句"尚未生成"
          if (!section?.body?.trim()) setTab("prompt");
        } else {
          setContent(document.content);
          setSavedContent(document.content);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : "加载失败"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [conversationId, chapter]);

  const fullPlainText = useMemo(() => toPlainText(fullContent), [fullContent]);

  const scopedAnnotations = useMemo(() => {
    if (!chapter) return annotations;
    // 优先用创建时记录的章节号（此时 quote_index 本来就是相对这一章算的，直接用）；
    // 没有记录（完整文档视图下创建的、或加这个字段之前的历史数据）才退回到按引用
    // 文本在全文中的出现位置去定位所属章节，并把 quote_index 换算成本章内的序号。
    const result: Annotation[] = [];
    for (const a of annotations) {
      if (a.chapter_num) {
        if (a.chapter_num === chapter.num) result.push(a);
        continue;
      }
      const loc = locateChapterForAnnotation(a, fullPlainText, chapters);
      if (!loc || loc.chapterNum !== chapter.num) continue;
      result.push(loc.localIndex === a.quote_index ? a : { ...a, quote_index: loc.localIndex });
    }
    return result;
  }, [annotations, fullPlainText, chapters, chapter]);

  const dirty = content !== savedContent;

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      if (chapter) {
        const sections = splitDocumentSections(fullContent, chapters).filter((s) => s.num !== chapter.num);
        sections.push({ num: chapter.num, title: chapter.title, body: content });
        const merged = mergeDocumentSections(sections, chapters);
        const doc = await saveDocumentContent(conversationId, merged);
        setFullContent(doc.content);
        const savedSection = splitDocumentSections(doc.content, chapters).find((s) => s.num === chapter.num);
        setContent(savedSection?.body ?? content);
        setSavedContent(savedSection?.body ?? content);
      } else {
        const doc = await saveDocumentContent(conversationId, content);
        setFullContent(doc.content);
        setSavedContent(doc.content);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function handleAdd(quote: string, comment: string, quoteIndex: number) {
    try {
      const annotation = await addAnnotation(conversationId, quote, comment, quoteIndex, chapter?.num ?? null);
      setAnnotations((prev) => [...prev, annotation]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "添加批注失败");
    }
  }

  async function toggleResolved(a: Annotation) {
    const next = !a.resolved;
    setAnnotations((prev) => prev.map((x) => (x.id === a.id ? { ...x, resolved: next ? 1 : 0 } : x)));
    try {
      await setAnnotationResolved(conversationId, a.id, next);
    } catch {
      setAnnotations((prev) => prev.map((x) => (x.id === a.id ? { ...x, resolved: a.resolved } : x)));
    }
  }

  async function removeAnnotation(a: Annotation) {
    setAnnotations((prev) => prev.filter((x) => x.id !== a.id));
    try {
      await deleteAnnotation(conversationId, a.id);
    } catch {
      setAnnotations((prev) => [...prev, a]);
    }
  }

  /** 反向跳转：点击右侧批注条目，定位到左侧正文里对应的高亮处。
   *  正文的高亮只在预览模式下才渲染，如果当时正处于编辑模式，就先切过去，
   *  等这次的 setMode 提交、预览区真正挂载出来之后，AnnotatedMarkdown 内部
   *  的 effect 会看到 scrollToId 已经设置，自己滚过去。 */
  function jumpToAnnotation(a: Annotation) {
    setActiveAnnoId(a.id);
    setMode("preview");
    setScrollToId(a.id);
  }

  return (
    <div className="doc-editor-overlay" onClick={onClose}>
      <div className={`doc-editor-panel ${fullscreen ? "fullscreen" : ""}`} onClick={(e) => e.stopPropagation()}>
        <header className="doc-editor-header">
          <div>
            <h2 className="doc-editor-title">
              {chapter ? `${chapter.num} ${chapter.title} · 预览与批注` : "方案文档（完整版）· 预览与批注"}
            </h2>
            <p className="doc-editor-sub">
              {!chapter
                ? "在预览模式下选中文字即可添加批注；切换到编辑模式可直接修改内容"
                : tab === "prompt"
                ? "选中提示词中的文字即可添加批注，批注对所有人可见"
                : "在预览模式下选中文字即可添加批注；切换到编辑模式可直接修改本章内容"}
            </p>
          </div>
          <div className="doc-editor-header-actions">
            <button
              className="btn btn-text history-modal-close"
              type="button"
              title={fullscreen ? "退出全屏" : "全屏"}
              onClick={() => setFullscreen((v) => !v)}
            >
              {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button className="btn btn-text history-modal-close" type="button" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </header>

        {chapter && (
          <div className="doc-editor-tabs">
            <button
              className={`doc-editor-tab ${tab === "doc" ? "active" : ""}`}
              type="button"
              onClick={() => setTab("doc")}
            >
              正文
            </button>
            <button
              className={`doc-editor-tab ${tab === "prompt" ? "active" : ""}`}
              type="button"
              onClick={() => setTab("prompt")}
            >
              提示词
            </button>
          </div>
        )}

        {chapter && tab === "prompt" ? (
          <PromptPane chapter={chapter} user={user} onPromptSaved={onPromptSaved} />
        ) : (
          <>
            <div className="doc-editor-toolbar">
              <div className="doc-editor-modes">
                <button
                  className={`btn btn-outlined doc-mode-btn ${mode === "preview" ? "active" : ""}`}
                  type="button"
                  onClick={() => setMode("preview")}
                >
                  预览 / 批注
                </button>
                <button
                  className={`btn btn-outlined doc-mode-btn ${mode === "edit" ? "active" : ""}`}
                  type="button"
                  onClick={() => setMode("edit")}
                >
                  <Pencil size={13} className="icon" />
                  <span>编辑</span>
                </button>
              </div>
              <button className="btn btn-clay" type="button" disabled={!dirty || saving} onClick={handleSave}>
                <Save size={14} className="icon" />
                <span>{saving ? "保存中…" : dirty ? "保存修改" : "已保存"}</span>
              </button>
            </div>

            {error && <p className="inline-hint doc-editor-error">{error}</p>}

            <div className="doc-editor-body">
              <div className="doc-editor-main">
                {loading ? (
                  <p className="admin-empty">加载中…</p>
                ) : chapter && !content.trim() && mode === "preview" ? (
                  <p className="admin-empty">该章节尚未生成正文，可切换到编辑模式手动撰写</p>
                ) : mode === "edit" ? (
                  <textarea
                    className="doc-editor-textarea"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    spellCheck={false}
                  />
                ) : (
                  <AnnotatedMarkdown
                    content={content}
                    annotations={scopedAnnotations}
                    onAdd={handleAdd}
                    onActivate={setActiveAnnoId}
                    scrollToId={scrollToId}
                    onScrolledTo={() => setScrollToId(null)}
                  />
                )}
              </div>

              <AnnotationSidebar
                title="批注"
                annotations={scopedAnnotations}
                activeAnnoId={activeAnnoId}
                emptyHint="暂无批注，选中预览中的文字即可添加"
                onToggleResolved={toggleResolved}
                onDelete={removeAnnotation}
                onJump={jumpToAnnotation}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
